/**
 * Moodle Import Page Component
 * Upload Moodle JSON export and mark learners individually
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControl, InputLabel,
  Select, MenuItem, Card, CardContent, Divider, List, ListItem,
  ListItemText, ListItemButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, LinearProgress,
  FormControlLabel, Radio, RadioGroup, FormLabel, Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthenticatedRequest } from '../../hooks/useAuthenticatedRequest';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import GradingIcon from '@mui/icons-material/Grading';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MissingSubmissionsReport from '../shared/MissingSubmissionsReport';

interface MoodleLearner {
  firstname: string;
  lastname: string;
  email: string;
  full_name: string;
  status: string;
  started: string;
  completed: string;
  duration: string;
  grade: string;
  responses: { [key: string]: string };
  response_count: number;
  matched_learner_id: number | null;
  matched_learner_name: string | null;
}

interface Assignment {
  id: number;
  title: string;
  rubric_id: number | null;
}

interface Rubric {
  id: number;
  name: string;
}

interface MarkingResult {
  success: boolean;
  submission_id: number;
  learner_name: string;
  ai_score: string;
  ai_feedback: any;
  error?: string;
}

const MoodleImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { request, isReady } = useAuthenticatedRequest();

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'text'>('file');
  const [fileType, setFileType] = useState<'json' | 'html'>('json');
  const [uploading, setUploading] = useState(false);

  // Learner data
  const [learners, setLearners] = useState<MoodleLearner[]>([]);
  const [selectedLearner, setSelectedLearner] = useState<MoodleLearner | null>(null);
  const [allLearners, setAllLearners] = useState<any[]>([]); // All Caliper learners for manual matching
  const [manualMatches, setManualMatches] = useState<{ [key: string]: number | null }>({}); // Track manual selections
  const [markedLearners, setMarkedLearners] = useState<Set<string>>(new Set()); // Track which learners have been marked

  // Assignment/Rubric selection
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [selectedRubric, setSelectedRubric] = useState<Rubric | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  // Marking state
  const [marking, setMarking] = useState(false);
  const [markingResult, setMarkingResult] = useState<MarkingResult | null>(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  // Submission action dialog
  const [showSubmissionActionDialog, setShowSubmissionActionDialog] = useState(false);
  const [submissionAction, setSubmissionAction] = useState<'create_only' | 'create_and_mark'>('create_only');

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Add to Class state
  const [classes, setClasses] = useState<any[]>([]);
  const [showAddToClassDialog, setShowAddToClassDialog] = useState(false);
  const [learnerToAdd, setLearnerToAdd] = useState<MoodleLearner | null>(null);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [addingToClass, setAddingToClass] = useState(false);
  const [actionAfterAdding, setActionAfterAdding] = useState<'mark_now' | 'create_submission' | 'add_only'>('mark_now');

  // Load assignments, rubrics, and classes on mount
  useEffect(() => {
    if (!isReady) return;

    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [assignmentsRes, rubricsRes, classesRes, learnersRes] = await Promise.all([
          request(() => axios.get('/api/assignments/')),
          request(() => axios.get('/api/rubrics/')),
          request(() => axios.get('/api/school-classes/')),
          request(() => axios.get('/api/learners/'))
        ]);
        setAssignments(assignmentsRes.data.results || []);
        setRubrics(rubricsRes.data.results || []);
        setClasses(classesRes.data.results || []);
        setAllLearners(learnersRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load assignments and rubrics');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Auto-select rubric when assignment changes
  useEffect(() => {
    if (selectedAssignment?.rubric_id) {
      const rubric = rubrics.find(r => r.id === selectedAssignment.rubric_id);
      if (rubric) {
        setSelectedRubric(rubric);
      }
    }
  }, [selectedAssignment, rubrics]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleParseJson = async () => {
    setError(null);
    setLearners([]);

    if (uploadMode === 'file' && !selectedFile) {
      setError(`Please select a ${fileType.toUpperCase()} file`);
      return;
    }

    if (uploadMode === 'text' && !jsonText.trim()) {
      setError('Please paste JSON data');
      return;
    }

    setUploading(true);

    try {
      let response;

      if (uploadMode === 'file') {
        const formData = new FormData();
        formData.append('file', selectedFile!);
        formData.append('file_type', fileType);

        // Use different endpoint based on file type
        const endpoint = fileType === 'html' ? '/api/moodle/parse-html/' : '/api/moodle/parse/';
        response = await request(() => axios.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }));
      } else {
        response = await request(() => axios.post('/api/moodle/parse/', {
          json_data: jsonText
        }));
      }

      if (response.data.success) {
        setLearners(response.data.learners);
        if (response.data.learners.length === 0) {
          setError('No learners found in the data');
        }
      } else {
        setError(response.data.error || `Failed to parse ${fileType.toUpperCase()}`);
      }
    } catch (err: any) {
      console.error('Error parsing Moodle data:', err);
      setError(err.response?.data?.error || `Failed to parse Moodle ${fileType.toUpperCase()}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSelectLearner = (learner: MoodleLearner) => {
    setSelectedLearner(learner);
    setMarkingResult(null);
    setError(null);
  };

  const handleManualMatch = (moodleLearner: MoodleLearner, caliperLearnerId: number | null) => {
    // Create a unique key for this Moodle learner
    const learnerKey = `${moodleLearner.email}_${moodleLearner.full_name}`;

    // Update manual matches
    setManualMatches(prev => ({
      ...prev,
      [learnerKey]: caliperLearnerId
    }));

    // Update the learner in the list with the manual match
    setLearners(prevLearners =>
      prevLearners.map(l => {
        if (l.email === moodleLearner.email && l.full_name === moodleLearner.full_name) {
          const matchedLearner = allLearners.find(al => al.id === caliperLearnerId);
          return {
            ...l,
            matched_learner_id: caliperLearnerId,
            matched_learner_name: matchedLearner ? `${matchedLearner.first_name} ${matchedLearner.last_name}` : null
          };
        }
        return l;
      })
    );

    // Update selected learner if it's the one being matched
    if (selectedLearner && selectedLearner.email === moodleLearner.email && selectedLearner.full_name === moodleLearner.full_name) {
      const matchedLearner = allLearners.find(al => al.id === caliperLearnerId);
      setSelectedLearner({
        ...selectedLearner,
        matched_learner_id: caliperLearnerId,
        matched_learner_name: matchedLearner ? `${matchedLearner.first_name} ${matchedLearner.last_name}` : null
      });
    }
  };

  const handleMarkLearner = async () => {
    if (!selectedLearner) {
      setError('Please select a learner');
      return;
    }

    // Determine learner_id
    const learnerId = selectedLearner.matched_learner_id;
    if (!learnerId) {
      setError('This learner is not matched to any Caliper learner. Please select a learner manually or create a new one.');
      return;
    }

    // Check if assignment is selected
    if (!selectedAssignment) {
      setError('Please select an assignment');
      return;
    }

    // Determine default action based on what's available
    if (selectedRubric) {
      setSubmissionAction('create_and_mark'); // Can mark with AI if rubric available
    } else {
      setSubmissionAction('create_only'); // Only create submission if no rubric
    }

    // Show dialog to choose action
    setShowSubmissionActionDialog(true);
  };

  const handleSubmissionAction = async () => {
    if (!selectedLearner || !selectedLearner.matched_learner_id) {
      setError('Please select a learner');
      return;
    }

    setShowSubmissionActionDialog(false);

    if (submissionAction === 'create_and_mark') {
      await markLearner(selectedLearner, selectedLearner.matched_learner_id);
    } else {
      await createSubmission(selectedLearner, selectedLearner.matched_learner_id);
    }
  };

  const handleCloseResultDialog = () => {
    setShowResultDialog(false);
  };

  const handleMarkNext = () => {
    setShowResultDialog(false);
    setMarkingResult(null);

    // Find next unmatched or next learner
    const currentIndex = learners.findIndex(l => l === selectedLearner);
    if (currentIndex < learners.length - 1) {
      setSelectedLearner(learners[currentIndex + 1]);
    }
  };

  const handleOpenAddToClass = (learner: MoodleLearner) => {
    setLearnerToAdd(learner);
    setSelectedClass(null);

    // Auto-select the appropriate action based on what's available
    if (selectedAssignment && selectedRubric) {
      setActionAfterAdding('mark_now'); // Can mark immediately
    } else if (selectedAssignment) {
      setActionAfterAdding('create_submission'); // Can create submission
    } else {
      setActionAfterAdding('add_only'); // Can only add to class
    }

    setShowAddToClassDialog(true);
  };

  const handleAddToClass = async () => {
    if (!learnerToAdd || !selectedClass) {
      setError('Please select a class');
      return;
    }

    // Validate prerequisites based on action
    if (actionAfterAdding === 'mark_now') {
      if (!selectedAssignment || !selectedRubric) {
        setError('Please select an assignment and rubric before marking');
        return;
      }
    } else if (actionAfterAdding === 'create_submission') {
      if (!selectedAssignment) {
        setError('Please select an assignment before creating submission');
        return;
      }
    }

    setAddingToClass(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/create-learner/', {
        firstname: learnerToAdd.firstname,
        lastname: learnerToAdd.lastname,
        email: learnerToAdd.email,
        class_id: selectedClass
      }));

      // Update the learner in the list
      const updatedLearner = {
        ...learnerToAdd,
        matched_learner_id: response.data.learner_id,
        matched_learner_name: response.data.learner_name
      };

      const updatedLearners = learners.map(l =>
        l === learnerToAdd ? updatedLearner : l
      );
      setLearners(updatedLearners);

      // If this is the currently selected learner, update it too
      if (selectedLearner === learnerToAdd) {
        setSelectedLearner(updatedLearner);
      }

      setShowAddToClassDialog(false);
      setLearnerToAdd(null);
      setSelectedClass(null);

      // Execute post-add action based on selection
      if (actionAfterAdding === 'mark_now' && selectedAssignment && selectedRubric) {
        // Mark immediately with AI
        setSelectedLearner(updatedLearner);
        setTimeout(async () => {
          await markLearner(updatedLearner, response.data.learner_id);
        }, 500);
      } else if (actionAfterAdding === 'create_submission' && selectedAssignment) {
        // Create submission without marking for later workflow (rubric optional)
        setSelectedLearner(updatedLearner);
        setTimeout(async () => {
          await createSubmission(updatedLearner, response.data.learner_id);
        }, 500);
      }
      // If 'add_only', do nothing else
    } catch (err: any) {
      console.error('Error adding learner to class:', err);
      setError(err.response?.data?.error || 'Failed to add learner to class');
    } finally {
      setAddingToClass(false);
    }
  };

  // Extracted marking logic for reuse
  const markLearner = async (learner: MoodleLearner, learnerId: number) => {
    if (!selectedAssignment || !selectedRubric) {
      setError('Please select an assignment and rubric');
      return;
    }

    setMarking(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/mark-learner/', {
        learner_id: learnerId,
        assignment_id: selectedAssignment.id,
        rubric_id: selectedRubric.id,
        responses: learner.responses
      }));

      setMarkingResult(response.data);
      setShowResultDialog(true);

      // Mark this learner as processed
      const learnerKey = `${learner.email}_${learner.full_name}`;
      setMarkedLearners(prev => new Set(prev).add(learnerKey));
    } catch (err: any) {
      console.error('Error marking learner:', err);
      setError(err.response?.data?.error || 'Failed to mark learner');
    } finally {
      setMarking(false);
    }
  };

  // Create submission without marking for later workflow
  const createSubmission = async (learner: MoodleLearner, learnerId: number) => {
    if (!selectedAssignment) {
      setError('Please select an assignment');
      return;
    }

    setMarking(true);
    setError(null);

    try {
      const response = await request(() => axios.post('/api/moodle/create-submission/', {
        learner_id: learnerId,
        assignment_id: selectedAssignment.id,
        rubric_id: selectedRubric?.id || null,  // Rubric is optional for later marking
        responses: learner.responses
      }));

      // Show success message
      setMarkingResult({
        success: true,
        submission_id: response.data.submission_id,
        learner_name: response.data.learner_name,
        ai_score: 'Not yet marked',
        ai_feedback: { message: 'Submission created successfully. Teacher can mark this later in the Submissions workflow.' }
      });
      setShowResultDialog(true);

      // Mark this learner as processed
      const learnerKey = `${learner.email}_${learner.full_name}`;
      setMarkedLearners(prev => new Set(prev).add(learnerKey));
    } catch (err: any) {
      console.error('Error creating submission:', err);
      setError(err.response?.data?.error || 'Failed to create submission');
    } finally {
      setMarking(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Dashboard
        </MuiLink>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/fast-mark')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Fast Mark
        </MuiLink>
        <Typography color="text.primary">Moodle Import</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Moodle Import</Typography>
        <Typography variant="body2" color="textSecondary">
          Upload Moodle quiz export JSON, select learners, and mark them individually with AI assistance
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Step 1: Upload JSON */}
      {learners.length === 0 && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Step 1: Upload Moodle Data</Typography>

          <Stack spacing={2} sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Upload Method</InputLabel>
              <Select
                value={uploadMode}
                onChange={(e) => setUploadMode(e.target.value as 'file' | 'text')}
                label="Upload Method"
              >
                <MenuItem value="file">Upload File</MenuItem>
                <MenuItem value="text">Paste JSON Text</MenuItem>
              </Select>
            </FormControl>

            {uploadMode === 'file' && (
              <FormControl fullWidth>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={fileType}
                  onChange={(e) => {
                    setFileType(e.target.value as 'json' | 'html');
                    setSelectedFile(null); // Reset file when changing type
                  }}
                  label="File Type"
                >
                  <MenuItem value="json">JSON Export</MenuItem>
                  <MenuItem value="html">HTML Export</MenuItem>
                </Select>
              </FormControl>
            )}

            {uploadMode === 'file' ? (
              <Box>
                <input
                  accept={fileType === 'json' ? '.json' : '.html,.htm'}
                  style={{ display: 'none' }}
                  id="moodle-file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="moodle-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    {selectedFile ? selectedFile.name : `Choose ${fileType.toUpperCase()} File`}
                  </Button>
                </label>
              </Box>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Paste JSON Data"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='[{"firstname":"John","lastname":"Doe","emailaddress":"john@example.com",...}]'
              />
            )}

            <Button
              variant="contained"
              onClick={handleParseJson}
              disabled={uploading || (uploadMode === 'file' && !selectedFile) || (uploadMode === 'text' && !jsonText.trim())}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Parsing...' : 'Parse Moodle Data'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Step 2: Select Assignment & Rubric */}
      {learners.length > 0 && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Step 2: Select Assignment & Rubric</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Assignment</InputLabel>
              <Select
                value={selectedAssignment?.id || ''}
                onChange={(e) => {
                  const assignment = assignments.find(a => a.id === e.target.value);
                  setSelectedAssignment(assignment || null);
                }}
                label="Assignment"
                disabled={loadingData}
              >
                <MenuItem value=""><em>Select assignment...</em></MenuItem>
                {assignments.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rubric</InputLabel>
              <Select
                value={selectedRubric?.id || ''}
                onChange={(e) => {
                  const rubric = rubrics.find(r => r.id === e.target.value);
                  setSelectedRubric(rubric || null);
                }}
                label="Rubric"
                disabled={loadingData}
              >
                <MenuItem value=""><em>Select rubric...</em></MenuItem>
                {rubrics.map(r => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {/* Missing Submissions Report */}
      {selectedAssignment && (
        <MissingSubmissionsReport assignmentId={selectedAssignment.id} />
      )}

      {/* Step 3: Learner List & Selection */}
      {learners.length > 0 && (
        <Box display="flex" gap={3}>
          {/* Learner List */}
          <Paper sx={{ flex: 1, p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Step 3: Select Learner ({learners.length} found)
              </Typography>
              {markedLearners.size > 0 && (
                <Chip
                  icon={<AssignmentTurnedInIcon />}
                  label={`${markedLearners.size} of ${learners.length} Marked`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            {!selectedAssignment && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Tip:</strong> Select an assignment in Step 2 above before adding learners.
                This allows you to create submissions immediately when adding unmatched learners.
              </Alert>
            )}
            <List>
              {learners.map((learner, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    selected={selectedLearner === learner}
                    onClick={() => handleSelectLearner(learner)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" />
                          {learner.full_name}
                          {(() => {
                            const learnerKey = `${learner.email}_${learner.full_name}`;
                            const isMarked = markedLearners.has(learnerKey);

                            return (
                              <>
                                {learner.matched_learner_id ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Matched"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                            <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
                              <Chip
                                icon={<WarningIcon />}
                                label="Not Matched"
                                color="warning"
                                size="small"
                              />
                              <Autocomplete
                                size="small"
                                options={allLearners}
                                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                                onChange={(e, value) => {
                                  e.stopPropagation();
                                  handleManualMatch(learner, value ? value.id : null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Select Existing Learner"
                                    size="small"
                                  />
                                )}
                                sx={{ minWidth: 250 }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Typography variant="caption" color="text.secondary">or</Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAddToClass(learner);
                                }}
                              >
                                Add to Class
                              </Button>
                            </Box>
                                )}
                                {isMarked && (
                                  <Chip
                                    icon={<AssignmentTurnedInIcon />}
                                    label="Marked"
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      }
                      secondary={
                        <>
                          {learner.email} • {learner.response_count} responses • {learner.status}
                          {learner.matched_learner_name && (
                            <span> • Matched: {learner.matched_learner_name}</span>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Learner Details & Submission Button */}
          {selectedLearner && (
            <Paper sx={{ flex: 1, p: 3 }}>
              <Typography variant="h6" gutterBottom>Step 4: Create Submission</Typography>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{selectedLearner.full_name}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Email: {selectedLearner.email}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Status: {selectedLearner.status}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Duration: {selectedLearner.duration}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Responses: {selectedLearner.response_count} questions
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Responses Preview:
                  </Typography>
                  {Object.entries(selectedLearner.responses).slice(0, 2).map(([qNum, answer]) => (
                    <Box key={qNum} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary">
                        Question {qNum}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {answer.substring(0, 100)}{answer.length > 100 ? '...' : ''}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={marking ? <CircularProgress size={20} color="inherit" /> : <GradingIcon />}
                onClick={handleMarkLearner}
                disabled={marking || !selectedAssignment || !selectedLearner.matched_learner_id}
              >
                {marking ? 'Processing...' : 'Create Submission'}
              </Button>

              {!selectedLearner.matched_learner_id && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This learner is not matched to any Caliper learner. Please create the learner in the system first.
                </Alert>
              )}

              {!selectedAssignment && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please select an assignment in Step 2 to create a submission.
                </Alert>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Results Dialog */}
      <Dialog open={showResultDialog} onClose={handleCloseResultDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          AI Marking Results - {markingResult?.learner_name}
        </DialogTitle>
        <DialogContent>
          {markingResult?.success ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Marking completed successfully!
              </Alert>

              <Typography variant="h6" gutterBottom>
                Score: {markingResult.ai_score}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Feedback:
              </Typography>

              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                  {JSON.stringify(markingResult.ai_feedback, null, 2)}
                </pre>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Submission ID: {markingResult.submission_id} - Review this marking in the submissions list
              </Alert>
            </>
          ) : (
            <Alert severity="error">
              {markingResult?.error || 'Marking failed'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultDialog}>Close</Button>
          <Button onClick={handleMarkNext} variant="contained" color="primary">
            Mark Next Learner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Action Dialog */}
      <Dialog open={showSubmissionActionDialog} onClose={() => setShowSubmissionActionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Submission Action</DialogTitle>
        <DialogContent>
          {selectedLearner && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>{selectedLearner.full_name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {selectedLearner.response_count} responses for {selectedAssignment?.title}
              </Typography>

              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="body2" fontWeight="bold">
                    What would you like to do with this submission?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  value={submissionAction}
                  onChange={(e) => setSubmissionAction(e.target.value as 'create_only' | 'create_and_mark')}
                >
                  <FormControlLabel
                    value="create_only"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Create submission only
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Save responses as a submission for later marking in the Submissions workflow (manual or AI)
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="create_and_mark"
                    control={<Radio />}
                    disabled={!selectedRubric}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Create submission and mark immediately with AI
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Create the submission and run AI marking right away
                        </Typography>
                        {!selectedRubric && (
                          <Typography variant="caption" color="error" display="block">
                            Requires a rubric to be selected in Step 2
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmissionActionDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmissionAction} variant="contained" color="primary">
            {submissionAction === 'create_and_mark' ? 'Create & Analyse with AI' : 'Create Submission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Class Dialog */}
      <Dialog open={showAddToClassDialog} onClose={() => setShowAddToClassDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Learner to Class</DialogTitle>
        <DialogContent>
          {learnerToAdd && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>{learnerToAdd.full_name}</strong> ({learnerToAdd.email})
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                This learner will be created in Caliper and enrolled in the selected class.
              </Typography>

              {!selectedAssignment && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>Note:</strong> No assignment selected in Step 2. You can only add this learner to a class.
                  To create a submission, select an assignment first and reopen this dialog.
                </Alert>
              )}

              <FormControl fullWidth>
                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass || ''}
                  onChange={(e) => setSelectedClass(Number(e.target.value))}
                  label="Select Class"
                  disabled={addingToClass}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name} - Grade {cls.grade_level} {cls.subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl component="fieldset" sx={{ mt: 3 }}>
                <FormLabel component="legend">
                  <Typography variant="body2" fontWeight="bold">
                    What to do after adding to class?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  value={actionAfterAdding}
                  onChange={(e) => setActionAfterAdding(e.target.value as 'mark_now' | 'create_submission' | 'add_only')}
                >
                  <FormControlLabel
                    value="mark_now"
                    control={<Radio />}
                    disabled={addingToClass || !selectedAssignment || !selectedRubric}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Mark immediately with AI
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Create submission and mark it right away using AI marking
                        </Typography>
                        {(!selectedAssignment || !selectedRubric) && (
                          <Typography variant="caption" color="error" display="block">
                            Requires assignment and rubric to be selected first
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="create_submission"
                    control={<Radio />}
                    disabled={addingToClass || !selectedAssignment}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Create submission for later marking
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Save learner's responses as submission - teacher can mark later in Submissions workflow
                        </Typography>
                        {!selectedAssignment && (
                          <Typography variant="caption" color="error" display="block">
                            Requires assignment to be selected first
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="add_only"
                    control={<Radio />}
                    disabled={addingToClass}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Add to class only
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Just create learner and enroll in class - no submission created
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {addingToClass && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="textSecondary">
                    {actionAfterAdding === 'mark_now' && 'Creating learner, enrolling in class, then marking with AI...'}
                    {actionAfterAdding === 'create_submission' && 'Creating learner, enrolling in class, then creating submission...'}
                    {actionAfterAdding === 'add_only' && 'Creating learner and enrolling in class...'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddToClassDialog(false)} disabled={addingToClass}>
            Cancel
          </Button>
          <Button onClick={handleAddToClass} variant="contained" color="primary" disabled={!selectedClass || addingToClass}>
            {actionAfterAdding === 'mark_now' && 'Add to Class & Mark'}
            {actionAfterAdding === 'create_submission' && 'Add to Class & Create Submission'}
            {actionAfterAdding === 'add_only' && 'Add to Class'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MoodleImportPage;
